import { Kushki } from '@kushki/js';
import { BankListResponse } from '@kushki/js/lib/types/bank_list_response';
import { TransferTokenRequest } from '@kushki/js/lib/types/remote/transfer_token_request';
import { REGIONAL_DOCUMENT_TYPE } from '../types/kushki-types';
import API_URLS from '../../../../../constants/api-urls';
import axios from '../../../../../utils/axios';
import session from '../../../../../utils/session';
import { LATINA_COUNTRY_CODE } from '../../../../../constants/latina-country';
import { getMobilePathParams } from '../utils/kushki';
import { getZeroInDecimal } from '../../../../../utils/price/get-zero-in-decimal';
import isMobileAppWithDeeplinkRedirect from '../../../../../utils/is-mobile-app';
import { isTest } from '../../../payment.helpers';
import { getUserOS, getUserSID } from '../../../../../services/quality-events/quality-events.helpers';
import serializeQueryParams from '../../../../../utils/url/serialize-query-params';
import { KushkiPaymentRequestType } from '../constants/kushki';

export async function getKushkiTransferToken(props: KushkiPaymentRequestType) {
    const {
        paymentOptionsData,
        kushkiFormData,
        invoiceFormData,
        withInvoice,
        price,
        user,
        currency
    } = props;
    const queryParams = serializeQueryParams();
    const region = paymentOptionsData?.userCountry;
    const finalPrice = getZeroInDecimal(Number(price.replace(',', '.')));
    const currencyCode = isTest() ? queryParams.currency : currency.code;
    const inMobileAppWithDeeplinkRedirect = isMobileAppWithDeeplinkRedirect() ? '&deepLinkRedirect=true' : '';

    const kushkiCheckout = new Kushki({
        inTestEnvironment: paymentOptionsData.kushkiEnv.inTestEnvironment,
        merchantId: paymentOptionsData.kushkiEnv.merchantId
    });

    const paramsMixin = {
        deepLinkRedirect: isMobileAppWithDeeplinkRedirect() ? true : undefined,
        email: session.isLoggedIn() ? undefined : invoiceFormData.email
    };

    if (isTest()) {
        Object.assign(paramsMixin, { countryCode: queryParams.region });
    }

    const kushkiCallBack = await axios.get(`${API_URLS.KUSHKI_TRANSFER_CALLBACK}${currencyCode}`, {
        params: { ...paramsMixin }
    });
    const { orderToken, callbackUrl } = kushkiCallBack;

    const transferTokenData: TransferTokenRequest = {
        amount: {
            iva: 0,
            subtotalIva: 0,
            subtotalIva0:
                region === LATINA_COUNTRY_CODE.CL || region === LATINA_COUNTRY_CODE.CO
                    ? finalPrice
                    : Number(price.replace(',', '.'))
        },
        callbackUrl,
        currency: currency as never,
        documentNumber: kushkiFormData.documentNumber,
        documentType: kushkiFormData.documentType as REGIONAL_DOCUMENT_TYPE['mexico'],
        email: user?.email ?? invoiceFormData.email,
        paymentDescription: 'Pago de bienes en eduki.com',
        userType: kushkiFormData.userType
    };

    if (region === LATINA_COUNTRY_CODE.CO) {
        transferTokenData.bankId = kushkiFormData.bankId;
    }

    if (region === LATINA_COUNTRY_CODE.MX) {
        delete transferTokenData.callbackUrl;
    }

    return new Promise((resolve) =>
        kushkiCheckout.requestTransferToken(
            transferTokenData,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            async (response: any) => {
                if (response.token) {
                    const postBody = () => {
                        if (session.isLoggedIn()) {
                            return withInvoice
                                ? { token: response.token, ...invoiceFormData, sid: getUserSID(), ...(getUserOS() && { os: getUserOS() }) }
                                : { acceptAgb: invoiceFormData.acceptAgb, token: response.token, sid: getUserSID(), ...(getUserOS() && { os: getUserOS() }) };
                        }
                        const paymentFormData = withInvoice
                            ? invoiceFormData
                            : { acceptAgb: invoiceFormData.acceptAgb, email: invoiceFormData.email };

                        return { ...paymentFormData, token: response.token, sid: getUserSID(), ...(getUserOS() && { os: getUserOS() }) };
                    };

                    const countryCoreParam = queryParams.region ? `&countryCode=${queryParams.region}` : '';
                    const data = await axios.post(
                        `${API_URLS.PAYMENT_ATTEMPT}/KushkiTransfer?currencyCode=${currency}&orderToken=${orderToken}${countryCoreParam}${inMobileAppWithDeeplinkRedirect}${getMobilePathParams()}`,
                        postBody()
                    );

                    resolve(data);
                } else {
                    console.error('Error: ', response.error, 'Code: ', response.code, 'Message: ', response.message);
                    resolve({ code: response.code, error: response.error, message: response.message });
                }
            }
        )
    );
}

export function getKushkiBankList(kushkiEnv: Kushki): Promise<BankListResponse> {
    return new Promise((resolve) => {
        kushkiEnv.requestBankList(async (response) => {
            resolve(response as BankListResponse);
        });
    });
}
